import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Divider,
  TablePagination,
  Checkbox,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  PersonAdd,
  School,
  PendingActions,
  AssignmentTurnedIn,
  Business,
  Download,
  AccountCircle,
  Settings,
  ExitToApp,
  Home,
  ArrowBack,
  List as ListIcon,
  Assessment,
  MenuBook,
  BarChart,
  ExpandMore,
  ExpandLess,
  Dashboard as DashboardIcon,
  AssignmentInd,
  PeopleAlt,
  ListAlt,
  NoteAdd,
  HowToReg,
  Insights,
  PersonSearch,
  ManageSearch,
  FamilyRestroom,
  DateRange,
  CloudUpload,
  GroupAdd,
  Print,
  PictureAsPdf,
  TableChart,
  Verified,
  Lock,
  Info,
  ContactPhone,
  CreditCard,
  Description,
  Phone,
  TrendingUp,
  Cake,
  Group,
  CalendarMonth,
  FileDownload,
  Edit,
  Delete,
  SwapHoriz,
  Apps,
  DeleteForever,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllAdmissions, getAdmissionStats, updateAdmissionStatus, approveAndEnroll, rejectAdmission, deleteAdmission, bulkSoftDeleteAdmissions, restoreAdmissions, bulkUpdateStatus, permanentlyDeleteAdmission, bulkPermanentlyDeleteAdmissions } from '../services/admissionService';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../config/api';
import { getAvailableModules } from '../config/modules';
import AdmissionCharts from '../components/admissions/AdmissionCharts';
import InstitutionSwitcher from '../components/InstitutionSwitcher';
import AdmissionByDateReport from '../components/reports/AdmissionByDateReport';
import AdmissionByMonthReport from '../components/reports/AdmissionByMonthReport';
import { useTablePagination } from '../hooks/useTablePagination';
import { notifyError, notifySuccess, notifyInfo } from '../utils/notify';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/constants';


const Admissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const availableModules = getAvailableModules();
  
  // Get section from URL path
  const getSectionFromPath = () => {
    const path = location.pathname;
    if (path === '/admissions' || path === '/admissions/') return 'list';
    if (path === '/admissions/reports') return 'reports';
    if (path === '/admissions/register') return 'register';
    if (path === '/admissions/analytics') return 'analytics';
    if (path === '/admissions/students/search-all') return 'search-all-data';
    if (path === '/admissions/students/bulk-signup') return 'bulk-signup';
    return 'list'; // Default
  };

  // Helper function to get URL for a section
  const getSectionUrl = (section) => {
    const urlMap = {
      'list': '/admissions',
      'new': '/admissions/new', // This goes to AdmissionForm page, not Admissions page
      'reports': '/admissions/reports',
      'register': '/admissions/register',
      'analytics': '/admissions/analytics',
      'search-all-data': '/admissions/students/search-all',
      'bulk-signup': '/admissions/students/bulk-signup',
    };
    return urlMap[section] || '/admissions';
  };

  // Helper function to navigate to a section
  const navigateToSection = (section) => {
    const url = getSectionUrl(section);
    navigate(url);
  };
  
  const [admissions, setAdmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // For list page - single status
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', remarks: '', effectiveDate: new Date().toISOString().split('T')[0] });
  const [anchorEl, setAnchorEl] = useState(null);
  const [modulesAnchorEl, setModulesAnchorEl] = useState(null);
  const [studentMenuAnchor, setStudentMenuAnchor] = useState(null);
  const [selectedStudentForMenu, setSelectedStudentForMenu] = useState(null);
  const [searchType, setSearchType] = useState('all'); // 'all', 'studentId', 'applicationNumber', 'name', 'email'
  const [activeSection, setActiveSection] = useState(getSectionFromPath()); // 'list', 'new', 'reports', 'register', 'analytics'
  const [admissionMenuOpen, setAdmissionMenuOpen] = useState(true);
  const [studentMenuOpen, setStudentMenuOpen] = useState(false);
  
  // Bulk operations state
  const [selectedAdmissions, setSelectedAdmissions] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  // Admissions Register Filters
  const [filterStudentName, setFilterStudentName] = useState('');
  const [filterRollNumber, setFilterRollNumber] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNewAdmissions, setFilterNewAdmissions] = useState(false);

  // Search Student All Data state
  const allStudentStatusOptions = [
    'Enrolled',
    'Struck Off',
    'Passout'
  ];
  const [allDataStatusFilter, setAllDataStatusFilter] = useState(allStudentStatusOptions);
  const [allDataSearchTerm, setAllDataSearchTerm] = useState('');

  const allDataFieldOptions = [
    { id: 'studentId', label: 'Id' },
    { id: 'studentName', label: 'Student name' },
    { id: 'fatherName', label: 'Father name' },
    { id: 'dateOfBirth', label: 'Date of birth' },
    { id: 'admissionNumber', label: 'Admission number' },
    { id: 'admissionDate', label: 'Admission date' },
    { id: 'rollNumber', label: 'Roll number' },
    { id: 'className', label: 'Class' },
    { id: 'sectionName', label: 'Section' },
    { id: 'gender', label: 'Gender' },
  ];
  const [selectedAllDataFields, setSelectedAllDataFields] = useState(
    allDataFieldOptions.map((f) => f.id)
  );

  // Pagination hooks for all tables (default: 12 rows per page)
  const admissionsListPagination = useTablePagination(12);
  const searchAllDataPagination = useTablePagination(12);
  const registerPagination = useTablePagination(12);

  // Reset pagination when filters change
  useEffect(() => {
    admissionsListPagination.resetPagination();
  }, [selectedStatus, selectedInstitution, searchTerm, searchType]);



  useEffect(() => {
    searchAllDataPagination.resetPagination();
  }, [allDataStatusFilter, selectedAllDataFields]);

  useEffect(() => {
    registerPagination.resetPagination();
  }, [searchTerm]);

  const [studentAllDataRows, setStudentAllDataRows] = useState([]);
  const [rawStudentAllData, setRawStudentAllData] = useState([]);

  // Report ref


  // Extract filtering logic to a reusable function for register tab
  const getFilteredRegisterAdmissions = () => {
    return admissions.filter((admission) => {
      // Always filter out soft-deleted records since the UI toggle is removed
      if (admission.isActive === false) return false;

      // Filter by last 30 days of admission
      if (filterNewAdmissions) {
        const dateStr = admission.admissionDate || admission.createdAt;
        const admissionDate = dateStr ? new Date(dateStr) : null;
        if (!admissionDate || isNaN(admissionDate.getTime())) return false;

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        if (admissionDate < thirtyDaysAgo) return false;
      }
      
      // Filter by Student Name
      if (filterStudentName) {
        const studentName = `${admission.personalInfo?.firstName || ''} ${admission.personalInfo?.middleName || ''} ${admission.personalInfo?.lastName || ''}`.toLowerCase();
        const admissionName = (admission.personalInfo?.name || '').toLowerCase();
        const searchName = filterStudentName.toLowerCase();
        if (!studentName.includes(searchName) && !admissionName.includes(searchName)) {
          return false;
        }
      }

      // Filter by Roll Number
      if (filterRollNumber) {
        const rollNumber = (admission.studentId?.rollNumber || '').toString().toLowerCase();
        if (!rollNumber.includes(filterRollNumber.toLowerCase())) {
          return false;
        }
      }

      // Filter by Class
      if (filterClass) {
        const classId = admission.class?._id || admission.class;
        if (classId !== filterClass) {
          return false;
        }
      }

      // Filter by Status
      if (filterStatus) {
        const matchStatus = (status) => {
          if (!status) return false;
          return status.toLowerCase().replace(/_/, '') === filterStatus.toLowerCase().replace(/_/, '');
        };
        if (!matchStatus(admission.status) && !matchStatus(admission.studentId?.status)) {
          return false;
        }
      }

      // Filter by Search Term (Global Search)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const studentName = `${admission.personalInfo?.firstName || ''} ${admission.personalInfo?.middleName || ''} ${admission.personalInfo?.lastName || ''}`.toLowerCase();
        const fatherName = (admission.guardianInfo?.fatherName || '').toLowerCase();
        const admissionNo = (admission.applicationNumber || '').toLowerCase();
        const phone = (admission.contactInfo?.phone || '').toLowerCase();
        const admissionName = (admission.personalInfo?.name || '').toLowerCase();
        if (!studentName.includes(search) && !fatherName.includes(search) && !admissionNo.includes(search) && !phone.includes(search) && !admissionName.includes(search)) return false;
      }

      return true;
    });
  };

  const filteredRegisterAdmissions = getFilteredRegisterAdmissions();

  const [registerSortConfig, setRegisterSortConfig] = useState({ key: '', direction: 'asc' });

  const handleRegisterSort = (key) => {
    setRegisterSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedRegisterAdmissions = React.useMemo(() => {
    const data = [...filteredRegisterAdmissions];
    if (!registerSortConfig.key) return data;

    data.sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (registerSortConfig.key) {
        case 'admissionDate':
          const rawDateA = a.admissionDate || a.createdAt;
          const rawDateB = b.admissionDate || b.createdAt;
          aVal = rawDateA ? new Date(rawDateA).getTime() : 0;
          bVal = rawDateB ? new Date(rawDateB).getTime() : 0;
          break;
        case 'appNumber':
          aVal = parseInt(a.applicationNumber || a.admissionNo || a.admissionNumber || 0, 10);
          bVal = parseInt(b.applicationNumber || b.admissionNo || b.admissionNumber || 0, 10);
          if (isNaN(aVal)) aVal = (a.applicationNumber || a.admissionNo || '').toLowerCase();
          if (isNaN(bVal)) bVal = (b.applicationNumber || b.admissionNo || '').toLowerCase();
          break;
        case 'rollNumber':
          aVal = parseInt(a.studentId?.rollNumber || a.rollNumber || 0, 10);
          bVal = parseInt(b.studentId?.rollNumber || b.rollNumber || 0, 10);
          if (isNaN(aVal)) aVal = (a.studentId?.rollNumber || a.rollNumber || '').toLowerCase();
          if (isNaN(bVal)) bVal = (b.studentId?.rollNumber || b.rollNumber || '').toLowerCase();
          break;
        case 'studentName':
          aVal = (a.personalInfo?.name || a.name || a.studentName || a.firstName || '').toLowerCase();
          bVal = (b.personalInfo?.name || b.name || b.studentName || b.firstName || '').toLowerCase();
          break;
        case 'fatherName':
          aVal = (a.guardianInfo?.fatherName || a.fatherName || '').toLowerCase();
          bVal = (b.guardianInfo?.fatherName || b.fatherName || '').toLowerCase();
          break;
        case 'dateOfBirth':
          const rawA = a.personalInfo?.dateOfBirth || a.dateOfBirth || a.dob;
          const rawB = b.personalInfo?.dateOfBirth || b.dateOfBirth || b.dob;
          aVal = rawA ? new Date(rawA).getTime() : 0;
          bVal = rawB ? new Date(rawB).getTime() : 0;
          break;
        case 'phone':
          aVal = (a.contactInfo?.phone || a.phone || a.mobileNumber || '').toLowerCase();
          bVal = (b.contactInfo?.phone || b.phone || a.mobileNumber || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.status || 'pending').toLowerCase();
          bVal = (b.status || 'pending').toLowerCase();
          break;
        case 'className':
          aVal = (a.class?.name || a.className || a.program || '').toLowerCase();
          bVal = (b.class?.name || b.className || b.program || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return registerSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return registerSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [filteredRegisterAdmissions, registerSortConfig]);


  
  // Helper for selection
  const isSelected = (id) => selectedAdmissions.indexOf(id) !== -1;

  // Destructure pagination for easier access in register tab
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = registerPagination;
  
  // Reports state
  const [selectedReportTab, setSelectedReportTab] = useState(0);
  const [reportFilters, setReportFilters] = useState({
    startDate: '',
    endDate: '',
    month: '',
    year: new Date().getFullYear().toString(),
    class: '',
    institution: ''
  });
  const [reportData, setReportData] = useState({
    'admission-by-date': null,
    'admission-by-month': null,
    'admission-by-year': null,
    'class-wise-comparison': null,
    'date-wise-admission': null,
    'student-strength-month-wise': null,
  });
  const [reportLoading, setReportLoading] = useState(false);

  // Helper to build row data for "Search Student All Data"
  const buildStudentAllDataRow = (admission, index) => {
    const fullName = admission.personalInfo?.name || '';
    const dob = admission.personalInfo?.dateOfBirth
      ? new Date(admission.personalInfo.dateOfBirth).toISOString().split('T')[0]
      : '';
    const ageYears = admission.personalInfo?.dateOfBirth
      ? Math.floor(
          (new Date().getTime() - new Date(admission.personalInfo.dateOfBirth).getTime()) /
            (365.25 * 24 * 60 * 60 * 1000)
        )
      : null;

    return {
      _id: admission._id,
      index: index + 1,
      studentId: admission.studentId?.enrollmentNumber || '',
      studentName: fullName || 'N/A',
      fatherName: admission.guardianInfo?.fatherName || 'N/A',
      dateOfBirth: dob,
      bloodGroup: admission.personalInfo?.bloodGroup || '',
      hobbies: admission.hobbies || '',
      category: admission.personalInfo?.category || 'Default',
      familyNumber: admission.familyNumber || '',
      admissionDate: admission.createdAt
        ? new Date(admission.createdAt).toISOString().split('T')[0]
        : '',
      gender:
        admission.personalInfo?.gender
          ? admission.personalInfo.gender.charAt(0).toUpperCase() +
            admission.personalInfo.gender.slice(1)
          : '',
      packageName: admission.packageName || '',
      packageStart: admission.packageStartDate
        ? new Date(admission.packageStartDate).toISOString().split('T')[0]
        : '',
      packageEnd: admission.packageEndDate
        ? new Date(admission.packageEndDate).toISOString().split('T')[0]
        : '',
      sectionRollNumber: admission.sectionRollNumber || '',
      admissionNumber: admission.applicationNumber || '',
      rollNumber: admission.studentId?.rollNumber || '',
      sectionName: admission.section?.name || '',
      className: admission.class?.name || admission.program || '',
      groupName: admission.group?.name || '',
      age: ageYears !== null ? `${ageYears} Years` : '',
    };
  };

  const handleSearchStudentAllData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const filters = {};
      if (selectedInstitution) filters.institution = selectedInstitution;
      
      const admissionsData = await getAllAdmissions(filters);
      let allAdmissions = admissionsData.data || [];
      
      // Filter for enrolled ones that have students
      let studentsData = allAdmissions.filter(admission => admission.studentId && admission.isActive !== false);
      
      setRawStudentAllData(studentsData);
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError(err.response?.data?.message || 'Failed to fetch student data');
      setRawStudentAllData([]);
    } finally {
      setLoading(false);
    }
  };

  // Live filter effect for Search All Data
  useEffect(() => {
    if (!rawStudentAllData || rawStudentAllData.length === 0) {
      setStudentAllDataRows([]);
      return;
    }

    let filteredData = [...rawStudentAllData];

    const frontendToBackendMap = {
      'Pending': 'pending',
      'Enrolled': 'enrolled',
      'Struck Off': 'struckoff',
      'Passout': 'passout'
    };
    
    if (allDataStatusFilter && allDataStatusFilter.length > 0 && allDataStatusFilter.length < allStudentStatusOptions.length) {
      const mappedStatuses = allDataStatusFilter.map(s => frontendToBackendMap[s] || s.toLowerCase());
      filteredData = filteredData.filter(admission => {
        const currentStatus = admission.status;
        const studentStatus = admission.studentId?.status || currentStatus;
        return mappedStatuses.includes(currentStatus) || mappedStatuses.includes(studentStatus);
      });
    }
    
    if (allDataSearchTerm) {
      const lowerTerm = allDataSearchTerm.toLowerCase();
      filteredData = filteredData.filter(admission => {
        const studentName = admission.personalInfo?.name || admission.studentId?.user?.name || '';
        const rollNum = admission.studentId?.rollNumber || admission.rollNumber || '';
        const appNum = admission.applicationNumber || '';
        return studentName.toLowerCase().includes(lowerTerm) || 
               String(rollNum).toLowerCase().includes(lowerTerm) ||
               String(appNum).toLowerCase().includes(lowerTerm);
      });
    }
    
    const rows = filteredData.map((admission, index) =>
      buildStudentAllDataRow(admission, index)
    );
    setStudentAllDataRows(rows);
  }, [rawStudentAllData, allDataSearchTerm, allDataStatusFilter]);


  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';
  const isAdmin = user.role === 'super_admin' || user.role === 'admin';

  // Helper to get the correct institution ID
  const getInstitutionId = () => {
    // First priority: selectedInstitution from state
    if (selectedInstitution) {
      return selectedInstitution;
    }
    
    // Second priority: user.institution (extract _id if it's an object)
    if (user.institution) {
      const institutionId = typeof user.institution === 'object' ? user.institution._id : user.institution;
      return institutionId;
    }
    
    return null;
  };

  // Initialize institution on mount
  // Initialize institution on mount and sync with localStorage
  // Initialize institution on mount and sync with localStorage
  useEffect(() => {
    const checkInstitution = () => {
      const institutionData = localStorage.getItem('selectedInstitution');
      let institutionId = null;

      if (institutionData) {
        try {
          // Try to parse as JSON (expected format)
          const institution = JSON.parse(institutionData);
          institutionId = institution._id || institution;
        } catch (e) {
          // Fallback: If parsing fails, assume it's a plain string ID
          institutionId = institutionData;
        }
      } else if (!isSuperAdmin && user.institution) {
        // Fallback for non-super admins to their own institution
        institutionId = typeof user.institution === 'object' ? user.institution._id : user.institution;
      }

      if (institutionId) {
         setSelectedInstitution(prev => prev !== institutionId ? institutionId : prev);
      }
    };

    // Check immediately
    checkInstitution();

    // Set up interval to check for changes (since storage event only fires for other tabs)
    const intervalId = setInterval(checkInstitution, 1000);

    // Also listen for custom event if your app uses one, or window storage event
    const handleStorageChange = (e) => {
      if (e.key === 'selectedInstitution') {
        checkInstitution();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Run only once on mount, but sets up listeners

  // Update activeSection when URL path changes (e.g., browser back/forward, direct navigation)
  useEffect(() => {
    const sectionFromPath = getSectionFromPath();
    if (sectionFromPath !== activeSection) {
      setActiveSection(sectionFromPath);
    }
    
    // Process query parameters for pre-filtering
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam) {
      if (sectionFromPath === 'search-all-data') {
        setAllDataStatusFilter([statusParam]);
      } else if (sectionFromPath === 'register') {
        setFilterStatus(statusParam.toLowerCase().replace(' ', '_').replace('struck_off', 'struckoff'));
      } else if (sectionFromPath === 'list') {
        setSelectedStatus(statusParam.toLowerCase().replace(' ', '_'));
      }
    }

    // Process days query parameter for register section
    const daysParam = params.get('days');
    if (daysParam === '30' && sectionFromPath === 'register') {
      setFilterNewAdmissions(true);
    } else {
      setFilterNewAdmissions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Auto-fetch data when navigating to search-all-data section
  useEffect(() => {
    if (activeSection === 'search-all-data' && admissions.length > 0) {
      const params = new URLSearchParams(location.search);
      const hasStatusQuery = params.has('status');
      
      // Auto-search when section is active and we have admissions data
      // Only if no rows are currently displayed, and we came with a specific query
      if (rawStudentAllData.length === 0) {
        handleSearchStudentAllData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, admissions, location.search]);

  // Fetch data when filters change
  useEffect(() => {
    // For admin users, require institution to be set
    if (!isSuperAdmin && !selectedInstitution) {
      if (user.institution) {
        // If user has institution but it's not set yet, wait for it to be set
        return;
      } else {
        // If admin user has no institution, show error and stop loading
        setError('Your account is not assigned to an institution. Please contact administrator.');
        setLoading(false);
        return;
      }
    }

    // For super_admins, REQUIRE institution to be selected
    if (isSuperAdmin && !selectedInstitution) {
      setError('Please select an institution from the navbar to view admissions.');
      setLoading(false);
      return;
    }

    // Fetch data only if institution is set
    if (selectedInstitution) {
      fetchData();
    }
  }, [selectedInstitution, selectedDepartment, selectedStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Fetch institutions if super admin
      if (isSuperAdmin) {
        const instResponse = await axios.get(getApiUrl('institutions'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInstitutions(instResponse.data.data || []);
      }

      // Fetch departments
      // ... (abbreviated)

      // Fetch admissions
      const filters = {};
      if (selectedInstitution) filters.institution = selectedInstitution;
      if (selectedDepartment) filters.department = selectedDepartment;
      if (selectedStatus) filters.status = selectedStatus;
      if (searchTerm) filters.search = searchTerm;

      const admissionsData = await getAllAdmissions(filters);
      setAdmissions(admissionsData.data || []);

      // Fetch stats
      const statsFilters = {};
      if (selectedInstitution) statsFilters.institution = selectedInstitution;
      const statsData = await getAdmissionStats(statsFilters);
      setStats(statsData.data || {});
    } catch (err) {
      console.error('Error fetching admissions data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch admissions';
      setError(errorMessage);
      setAdmissions([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (type) => {
    try {
      if (type === 'approve') {
        await updateAdmissionStatus(selectedAdmission._id, 'approved', actionDialog.remarks);
      } else if (type === 'reject') {
        await rejectAdmission(selectedAdmission._id, actionDialog.remarks);
      } else if (type === 'enroll') {
        await approveAndEnroll(selectedAdmission._id);
      } else if (type === 'struck_off') {
        await updateAdmissionStatus(selectedAdmission._id, 'struck_off', actionDialog.remarks, actionDialog.effectiveDate);
      }

      setActionDialog({ open: false, type: '', remarks: '', effectiveDate: new Date().toISOString().split('T')[0] });
      setSelectedAdmission(null);
      fetchData();
      notifySuccess(`Admission ${type}ed successfully`);
    } catch (err) {
      const msg = err.response?.data?.message || `Failed to ${type} admission`;
      setError(msg);
      notifyError(msg);
    }
  };

  const handleDeleteAdmission = async (admissionId, studentName) => {
    if (!window.confirm(`Are you sure you want to permanently delete the admission for ${studentName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await permanentlyDeleteAdmission(admissionId);
      setError(''); // Clear any previous errors
      notifySuccess('Admission deleted successfully');
      fetchData(); // Refresh the list
    } catch (err) {
      console.error('Error deleting admission:', err);
      const msg = err.response?.data?.message || 'Failed to delete admission';
      setError(msg);
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Bulk soft delete handler
  const handleBulkSoftDelete = async () => {
    if (selectedAdmissions.length === 0) {
      notifyError('Please select admissions to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedAdmissions.length} selected admission(s)? They can be restored later.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await bulkSoftDeleteAdmissions(selectedAdmissions);
      notifySuccess(result.message || 'Admissions deleted successfully');
      setSelectedAdmissions([]);
      fetchData(); // Refresh the list
    } catch (err) {
      console.error('Error deleting admissions:', err);
      const msg = err.response?.data?.message || 'Failed to delete admissions';
      setError(msg);
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Bulk restore handler
  const handleBulkRestore = async () => {
    if (selectedAdmissions.length === 0) {
      notifyError('Please select admissions to restore');
      return;
    }

    if (!window.confirm(`Are you sure you want to restore ${selectedAdmissions.length} selected admission(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await restoreAdmissions(selectedAdmissions);
      notifySuccess(result.message || 'Admissions restored successfully');
      setSelectedAdmissions([]);
      fetchData(); // Refresh the list
    } catch (err) {
      console.error('Error restoring admissions:', err);
      const msg = err.response?.data?.message || 'Failed to restore admissions';
      setError(msg);
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Bulk permanent delete handler
  const handleBulkPermanentDelete = async () => {
    if (selectedAdmissions.length === 0) {
      notifyError('Please select admissions to permanently delete');
      return;
    }

    if (!window.confirm(`WARNING: Are you sure you want to PERMANENTLY delete ${selectedAdmissions.length} selected admission(s)?\n\nThis action CANNOT be undone and will delete ALL associated data including:\n- Student Profile\n- User Account\n- Fee Records\n- Exam Results\n\nClick OK to confirm permanent deletion.`)) {
      return;
    }

    // Double confirmation for safety
    if (!window.confirm(`Are you ABSOLUTELY SURE? This is your last chance to cancel.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await bulkPermanentlyDeleteAdmissions(selectedAdmissions);
      notifySuccess(result.message || 'Admissions permanently deleted');
      setSelectedAdmissions([]);
      fetchData(); // Refresh the list
    } catch (err) {
      console.error('Error permanently deleting admissions:', err);
      const msg = err.response?.data?.message || 'Failed to permanently delete admissions';
      setError(msg);
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (event, filteredAdmissions) => {
    if (event.target.checked) {
      const allIds = filteredAdmissions.map(adm => adm._id);
      setSelectedAdmissions(allIds);
    } else {
      setSelectedAdmissions([]);
    }
  };

  // Handle individual checkbox
  const handleSelectAdmission = (admissionId) => {
    setSelectedAdmissions(prev => {
      if (prev.includes(admissionId)) {
        return prev.filter(id => id !== admissionId);
      } else {
        return [...prev, admissionId];
      }
    });
  };

  const openActionDialog = (admission, type) => {
    setSelectedAdmission(admission);
    setActionDialog({ open: true, type, remarks: '' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      struck_off: 'error',
      approved: 'success',
      rejected: 'error',
      enrolled: 'primary',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedInstitution');
    navigate('/login');
  };

  const handleModulesMenu = (event) => {
    setModulesAnchorEl(event.currentTarget);
  };

  const handleModulesClose = () => {
    setModulesAnchorEl(null);
  };

  const handleModuleClick = (route) => {
    handleModulesClose();
    navigate(route);
  };

  const handleExport = () => {
    let dataToExport = sortedRegisterAdmissions;
    if (selectedAdmissions && selectedAdmissions.length > 0) {
      dataToExport = sortedRegisterAdmissions.filter(admission => selectedAdmissions.includes(admission._id));
    }

    if (!dataToExport || dataToExport.length === 0) {
      notifyInfo('No records to export');
      return;
    }

    const exportData = dataToExport.map((admission, index) => {
      const studentName = admission.personalInfo?.name || admission.name || admission.studentName || admission.firstName || '';
      const rawDob = admission.personalInfo?.dateOfBirth || admission.dateOfBirth || admission.dob;
      const dateOfBirth = rawDob 
        ? new Date(rawDob).toLocaleDateString('en-GB')
        : 'N/A';
      
      const ageYears = rawDob
        ? Math.floor((new Date().getTime() - new Date(rawDob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : null;

      const dateOfAdmissionRaw = admission.admissionDate || admission.createdAt;
      const admissionDate = dateOfAdmissionRaw 
        ? new Date(dateOfAdmissionRaw).toLocaleDateString('en-GB')
        : 'N/A';
      
      const className = admission.class?.name || admission.className || admission.program || '';
      const sectionName = admission.section?.name || '';
      const groupName = admission.group?.name || '';
      
      const appNumber = admission.applicationNumber || admission.admissionNo || admission.admissionNumber || '';
      const rollNumber = admission.studentId?.rollNumber || admission.rollNumber || '';
      const sectionRollNumber = admission.sectionRollNumber || '';
      
      const phone = admission.contactInfo?.phone || 
                    admission.contactDetails?.phone || 
                    admission.contactInfo?.alternatePhone || 
                    admission.guardianInfo?.father?.mobileNumber || 
                    admission.guardianInfo?.mother?.mobileNumber || 
                    admission.guardianInfo?.mobileNumber || 
                    admission.phone || 
                    admission.mobileNumber || 
                    '';

      const gender = admission.personalInfo?.gender
          ? admission.personalInfo.gender.charAt(0).toUpperCase() + admission.personalInfo.gender.slice(1)
          : '';

      return {
        'Sr No': index + 1,
        'Date Of Admission': admissionDate,
        'Admission No': appNumber,
        'Roll No': rollNumber,
        'Section Roll No': sectionRollNumber,
        'Student Name': studentName || 'N/A',
        'Gender': gender,
        'Date Of Birth': dateOfBirth,
        'Age (Years)': ageYears !== null ? ageYears : '',
        'Blood Group': admission.personalInfo?.bloodGroup || '',
        'Category': admission.personalInfo?.category || '',
        'Family Number': admission.familyNumber || '',
        'Father Name': admission.guardianInfo?.fatherName || admission.fatherName || 'N/A',
        'Mother Name': admission.guardianInfo?.motherName || '',
        'Phone No': phone,
        'Class': className,
        'Section': sectionName,
        'Group': groupName,
        'Status': (admission.status || 'pending').replace('_', ' ').toUpperCase()
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Admissions');
    XLSX.writeFile(workbook, 'Admissions_Register.xlsx');
    notifySuccess('Exported successfully');
  };

  // Bulk Status Update State & Handlers
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState('');
  const [bulkStatusRemarks, setBulkStatusRemarks] = useState('');
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState(new Date().toISOString().split('T')[0]);

  const handleOpenBulkStatusDialog = () => {
    if (selectedAdmissions.length === 0) return;
    setSelectedBulkStatus('');
    setBulkStatusRemarks('');
    setBulkEffectiveDate(new Date().toISOString().split('T')[0]);
    setBulkStatusDialogOpen(true);
  };

  const handleCloseBulkStatusDialog = () => {
    setBulkStatusDialogOpen(false);
  };

  const handleBulkStatusUpdate = async () => {
    if (!selectedBulkStatus) return;
    
    try {
      setLoading(true);
      const isStruckOff = selectedBulkStatus === 'struckoff' || selectedBulkStatus === 'struck_off';
      await bulkUpdateStatus(selectedAdmissions, selectedBulkStatus, bulkStatusRemarks, isStruckOff ? bulkEffectiveDate : null);
      // Wait a moment for DB propagation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      notifySuccess('Statuses updated successfully');
      setBulkStatusDialogOpen(false);
      setSelectedAdmissions([]);
      fetchData();
      
    } catch (err) {
      console.error('Bulk status update error:', err);
      const msg = err.response?.data?.message || 'Failed to update statuses';
      notifyError(msg);
      setLoading(false);
    }
  };

  // Report type mapping
  const reportTypes = [
    'admission-by-date',
    'admission-by-month',
    'admission-by-year',
    'class-wise-comparison',
    'date-wise-admission'
  ];

  // Fetch report data based on selected report type
  const fetchReportData = async (reportType) => {
    try {
      setReportLoading(true);
      const token = localStorage.getItem('token');
      
      const filters = {
        ...reportFilters,
        institution: getInstitutionId() || reportFilters.institution,
        reportType: reportType
      };

      const response = await axios.get(getApiUrl('admissions/reports'), {
        headers: { Authorization: `Bearer ${token}` },
        params: filters
      });

      setReportData(prev => ({
        ...prev,
        [reportType]: response.data.data
      }));
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data');
      setReportData(prev => ({
        ...prev,
        [reportType]: null
      }));
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateReport = (reportType) => {
    fetchReportData(reportType);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedReportTab(newValue);
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleExportReportPDF = () => {
    notifyInfo('PDF export coming soon!');
  };

  const handleExportReportExcel = () => {
    notifyInfo('Excel export coming soon!');
  };

  const filteredAdmissions = admissions.filter((admission) => {
    // Always filter out soft-deleted records if applicable
    if (admission.isActive === false) return false;

    // Apply status filter
    if (selectedStatus) {
      const matchStatus = (status) => {
        if (!status) return false;
        return status.toLowerCase().replace(/_/, '') === selectedStatus.toLowerCase().replace(/_/, '');
      };
      if (!matchStatus(admission.status) && !matchStatus(admission.studentId?.status)) {
        return false;
      }
    }

    // Apply Student Name / General Search filter
    const searchVal = (filterStudentName || searchTerm || '').trim().toLowerCase();
    if (searchVal) {
      const studentName = `${admission.personalInfo?.firstName || ''} ${admission.personalInfo?.middleName || ''} ${admission.personalInfo?.lastName || ''}`.toLowerCase();
      const fatherName = (admission.guardianInfo?.fatherName || '').toLowerCase();
      const admissionNo = (admission.applicationNumber || '').toLowerCase();
      const phone = (admission.contactInfo?.phone || '').toLowerCase();
      const admissionName = (admission.personalInfo?.name || '').toLowerCase();
      if (!studentName.includes(searchVal) && !fatherName.includes(searchVal) && !admissionNo.includes(searchVal) && !phone.includes(searchVal) && !admissionName.includes(searchVal)) {
        return false;
      }
    }

    // Apply Roll Number filter
    if (filterRollNumber) {
      const rollNumber = (admission.studentId?.rollNumber || admission.rollNumber || '').toString().toLowerCase();
      if (!rollNumber.includes(filterRollNumber.trim().toLowerCase())) {
        return false;
      }
    }

    // Apply Class filter
    if (filterClass) {
      const classId = admission.class?._id || admission.class;
      if (classId !== filterClass) {
        return false;
      }
    }

    return true;
  });

  if (loading && admissions.length === 0 && !error) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const sidebarWidth = 280;

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard', { state: { preserveFilters: true } })}
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#5568d3',
              bgcolor: '#667eea15'
            }
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
      {/* Quick Links for Academic Setup */}
      <Paper sx={{ p: 2, mb: 3, mx: 3, mt: 0, background: 'linear-gradient(135deg, #667eea15 0%, #764ba205 100%)', border: '1px solid #667eea30' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
            Academic Setup
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/admissions/new')}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  bgcolor: '#10b98115',
                },
              }}
            >
              ➕ New Admission
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/classes')}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5568d3',
                  bgcolor: '#667eea15',
                },
              }}
            >
              📚 Manage Classes
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/sections')}
              sx={{
                borderColor: '#764ba2',
                color: '#764ba2',
                '&:hover': {
                  borderColor: '#653a8b',
                  bgcolor: '#764ba215',
                },
              }}
            >
              📑 Manage Sections
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/groups')}
              sx={{
                borderColor: '#f093fb',
                color: '#f093fb',
                '&:hover': {
                  borderColor: '#d97ee4',
                  bgcolor: '#f093fb15',
                },
              }}
            >
              👥 Manage Groups
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Content based on active section */}
      {activeSection === 'list' && (
        <>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4, px: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea15 0%, #764ba205 100%)',
              border: '1px solid #667eea30',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">Total Applications</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#667eea">{stats.totalApplications || 0}</Typography>
                </Box>
                <School sx={{ fontSize: 40, color: '#667eea', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #ff980015 0%, #ff980005 100%)',
              border: '1px solid #ff980030',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">Pending</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#ff9800">{stats.pendingApplications || 0}</Typography>
                </Box>
                <PendingActions sx={{ fontSize: 40, color: '#ff9800', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #2196f315 0%, #2196f305 100%)',
              border: '1px solid #2196f330',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">Enrolled</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#2196f3">{stats.enrolledApplications || 0}</Typography>
                </Box>
                <PersonAdd sx={{ fontSize: 40, color: '#2196f3', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #e91e6315 0%, #e91e6305 100%)',
              border: '1px solid #e91e6330',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">Struck Off</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#e91e63">{stats.struckOffApplications || 0}</Typography>
                </Box>
                <Cancel sx={{ fontSize: 40, color: '#e91e63', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)',
              border: '1px solid #9c27b030',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">Pass Out</Typography>
                  <Typography variant="h4" fontWeight="bold" color="#9c27b0">{stats.passoutApplications || 0}</Typography>
                </Box>
                <School sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3, mx: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4, mx: 3 }}>
        {/* Header with Quick Stats */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Admission Applications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredAdmissions.length} of {admissions.length} applications
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`Total: ${stats.totalApplications || 0}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`Pending: ${stats.pendingApplications || 0}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`Enrolled: ${stats.enrolledApplications || 0}`}
              color="info"
              variant="outlined"
            />
            <Chip
              label={`Struck Off: ${stats.struckOffApplications || 0}`}
              color="error"
              variant="outlined"
            />
            <Chip
              label={`Pass Out: ${stats.passoutApplications || 0}`}
              color="secondary"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Filters
            </Typography>
            {(filterStudentName || filterRollNumber || filterClass || selectedStatus || searchTerm) && (
              <Button
                size="small"
                onClick={() => {
                  setFilterStudentName('');
                  setFilterRollNumber('');
                  setFilterClass('');
                  setSelectedStatus('');
                  setSearchTerm('');
                }}
                sx={{ textTransform: 'none', color: '#667eea', fontWeight: 600 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: '2fr 1.5fr 1.5fr 1.5fr'
              },
              gap: 2,
              alignItems: 'center'
            }}
          >
            <TextField
              fullWidth
              label="Search"
              placeholder="Name, app no, phone..."
              value={filterStudentName}
              onChange={(e) => setFilterStudentName(e.target.value)}
              size="small"
            />
            <TextField
              fullWidth
              label="Roll Number"
              placeholder="Search roll no..."
              value={filterRollNumber}
              onChange={(e) => setFilterRollNumber(e.target.value)}
              size="small"
            />
            <TextField
              fullWidth
              select
              label="Class"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true
              }}
              SelectProps={{
                displayEmpty: true
              }}
            >
              <MenuItem value="">All Classes</MenuItem>
              {Array.from(new Map(admissions
                .filter(a => a.class)
                .map(a => {
                  const classId = a.class?._id || a.class;
                  const className = a.class?.name || 'Unknown';
                  return [classId, { _id: classId, name: className }];
                })
              ).values()).map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>
                  {cls.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              label="Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true
              }}
              SelectProps={{
                displayEmpty: true
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="enrolled">Enrolled</MenuItem>
              <MenuItem value="struckoff">Struck Off</MenuItem>
              <MenuItem value="passout">Passout</MenuItem>
            </TextField>
          </Box>
        </Paper>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#667eea' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Admission No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Roll No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applicant Name</TableCell>

                {isSuperAdmin && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Institution</TableCell>}
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Class</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applied On</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAdmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center">
                    <Box py={4}>
                      <Typography variant="body2" color="text.secondary">
                        No admissions found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                admissionsListPagination.getPaginatedData(filteredAdmissions).map((admission) => (
                  <TableRow key={admission._id} hover>
                    <TableCell>
                      <Chip label={admission.applicationNumber} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {admission.rollNumber || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {admission.personalInfo?.name || 'N/A'}
                        {admission.guardianInfo?.fatherName && (
                          <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 0.5 }}>
                            {admission.guardianInfo.fatherName}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>

                    {isSuperAdmin && (
                      <TableCell>
                        <Typography variant="body2">{admission.institution?.name || 'N/A'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">{admission.class?.name || admission.program || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(admission.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={admission.status.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={getStatusColor(admission.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/admissions/view/${admission._id}`)}
                        title="View Details"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      {hasPermission(PERMISSIONS.ADMISSIONS.EDIT) && (admission.status === 'pending' || admission.status === 'struckoff') && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openActionDialog(admission, 'enroll')}
                          title="Enroll Student"
                        >
                          <PersonAdd fontSize="small" />
                        </IconButton>
                      )}
                      {hasPermission(PERMISSIONS.ADMISSIONS.EDIT) && admission.status === 'pending' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openActionDialog(admission, 'struck_off')}
                          title="Mark Struck Off"
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {filteredAdmissions.length > 0 && (
            <TablePagination
              component="div"
              count={filteredAdmissions.length}
              page={admissionsListPagination.page}
              onPageChange={admissionsListPagination.handleChangePage}
              rowsPerPage={admissionsListPagination.rowsPerPage}
              onRowsPerPageChange={admissionsListPagination.handleChangeRowsPerPage}
              rowsPerPageOptions={admissionsListPagination.rowsPerPageOptions}
              labelRowsPerPage="Rows per page:"
            />
          )}
        </TableContainer>
      </Paper>

      {/* Analytics Charts Section - Show graphs on list page */}
      {getInstitutionId() && (
        <Box sx={{ mb: 4, mt: 4, px: 3 }}>
          <AdmissionCharts 
            filters={{ institution: getInstitutionId() }} 
          />
        </Box>
      )}
      </>
      )}

      {/* Section: Search Student All Data */}
      {activeSection === 'search-all-data' && (
        <Box sx={{ mx: 3 }}>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              STUDENT ALL DATA REPORT
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Search
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Name, Roll No..."
                  value={allDataSearchTerm}
                  onChange={(e) => setAllDataSearchTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Student Status
                </Typography>
                <FormControl fullWidth>
                  <Select
                    multiple
                    value={allDataStatusFilter}
                    onChange={(e) => setAllDataStatusFilter(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return 'Select Status';
                      if (selected.length === allStudentStatusOptions.length) {
                        return `All selected (${selected.length})`;
                      }
                      return selected.join(', ');
                    }}
                  >
                    {allStudentStatusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Fields
                </Typography>
                <FormControl fullWidth>
                  <Select
                    multiple
                    value={selectedAllDataFields}
                    onChange={(e) => setSelectedAllDataFields(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return 'Select Columns';
                      if (selected.length === allDataFieldOptions.length) {
                        return `All selected (${selected.length})`;
                      }
                      const labels = allDataFieldOptions
                        .filter((opt) => selected.includes(opt.id))
                        .map((opt) => opt.label);
                      return labels.join(', ');
                    }}
                  >
                    {allDataFieldOptions.map((field) => (
                      <MenuItem key={field.id} value={field.id}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Results Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : studentAllDataRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No students match the current filters.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#667eea' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sr</TableCell>
                      {allDataFieldOptions
                        .filter((field) => selectedAllDataFields.includes(field.id))
                        .map((field) => (
                          <TableCell key={field.id} sx={{ color: 'white', fontWeight: 'bold' }}>
                            {field.label}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchAllDataPagination.getPaginatedData(studentAllDataRows).map((row, idx) => {
                      const actualIndex = searchAllDataPagination.page * searchAllDataPagination.rowsPerPage + idx;
                      return (
                        <TableRow key={row._id || idx} hover>
                          <TableCell>{actualIndex + 1}</TableCell>
                          {allDataFieldOptions
                            .filter((field) => selectedAllDataFields.includes(field.id))
                            .map((field) => (
                              <TableCell key={field.id}>
                                {row[field.id] || ''}
                              </TableCell>
                            ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {studentAllDataRows.length > 0 && (
                  <TablePagination
                    component="div"
                    count={studentAllDataRows.length}
                    page={searchAllDataPagination.page}
                    onPageChange={searchAllDataPagination.handleChangePage}
                    rowsPerPage={searchAllDataPagination.rowsPerPage}
                    onRowsPerPageChange={searchAllDataPagination.handleChangeRowsPerPage}
                    rowsPerPageOptions={searchAllDataPagination.rowsPerPageOptions}
                    labelRowsPerPage="Rows per page:"
                  />
                )}
              </TableContainer>
            )}

            {studentAllDataRows.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total: {studentAllDataRows.length}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Section: New Admission */}
      {activeSection === 'new' && (
        <Box sx={{ p: 4, mx: 3 }}>
          <Typography variant="h5" gutterBottom>New Admission Form</Typography>
          <Typography variant="body1" color="text.secondary">
            New Admission form will be displayed here. Click the button below to navigate to the admission form.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => navigate('/admissions/new')}
            sx={{ mt: 2 }}
          >
            Go to New Admission Form
          </Button>
        </Box>
      )}

      {/* Section: Reports */}
      {activeSection === 'reports' && (
        <Box sx={{ mx: 3 }}>
          {/* Header */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" fontWeight="bold">
                Admission Reports
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={handlePrintReport}
                  disabled={!reportData[reportTypes[selectedReportTab]]}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={handleExportReportPDF}
                  disabled={!reportData[reportTypes[selectedReportTab]]}
                  color="error"
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TableChart />}
                  onClick={handleExportReportExcel}
                  disabled={!reportData[reportTypes[selectedReportTab]]}
                  color="success"
                >
                  Excel
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Tabs Navigation */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 2,
            }}>
              {[
                { icon: <DateRange />, label: "Admission By Date", index: 0 },
                { icon: <BarChart />, label: "New Admission Detail By Month", index: 1 },
                { icon: <Assessment />, label: "Admission Analysis By Year", index: 2 },
                { icon: <School />, label: "New Admission Strength Comparison Class Wise", index: 3 },
                { icon: <ListIcon />, label: "New Admission Date Wise", index: 4 },
                { icon: <TrendingUp />, label: "Student's Strength Month Wise", index: 5 },
              ].map((tab) => (
                <Button
                  key={tab.index}
                  variant={selectedReportTab === tab.index ? "contained" : "outlined"}
                  startIcon={tab.icon}
                  onClick={() => handleTabChange(null, tab.index)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 2,
                    py: 1.5,
                    ...(selectedReportTab === tab.index ? {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                      },
                    } : {
                      borderColor: '#667eea',
                      color: '#667eea',
                      '&:hover': {
                        borderColor: '#5568d3',
                        bgcolor: '#667eea15',
                      },
                    }),
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Paper>

          {/* Tab Panel 0: Admission By Date */}
          {selectedReportTab === 0 && (
            <AdmissionByDateReport />
          )}

          {/* Tab Panel 1: Admission By Month */}
          {selectedReportTab === 1 && (
            <AdmissionByMonthReport />
          )}

          {/* Tab Panel 2: Admission By Year */}
          {selectedReportTab === 2 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Year"
                    type="number"
                    value={reportFilters.year}
                    onChange={(e) => setReportFilters({ ...reportFilters, year: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<Assessment />}
                    onClick={() => handleGenerateReport('admission-by-year')}
                    fullWidth
                    sx={{
                      height: '40px',
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5a6fd6' },
                      textTransform: 'none'
                    }}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>

              {reportLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!reportLoading && reportData['admission-by-year'] && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Year: {reportFilters.year}
                  </Typography>
                  
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#667eea15', border: '1px solid #667eea30' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Total Applications</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#667eea">
                            {reportData['admission-by-year'].summary?.total || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#ff980015', border: '1px solid #ff980030' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Pending</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#ff9800">
                            {reportData['admission-by-year'].summary?.pending || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#4caf5015', border: '1px solid #4caf5030' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Approved</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#4caf50">
                            {reportData['admission-by-year'].summary?.approved || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#2196f315', border: '1px solid #2196f330' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Enrolled</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#2196f3">
                            {reportData['admission-by-year'].summary?.enrolled || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Month</strong></TableCell>
                          <TableCell><strong>Applications</strong></TableCell>
                          <TableCell><strong>Conversion Rate</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData['admission-by-year'].monthlyData?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                No data available for the selected criteria
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportData['admission-by-year'].monthlyData?.map((row, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{row.month}</TableCell>
                              <TableCell><strong>{row.total}</strong></TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${row.conversionRate}%`} 
                                  size="small" 
                                  color={row.conversionRate > 50 ? 'success' : 'warning'}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!reportLoading && !reportData['admission-by-year'] && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Select year and click "Generate" to view the report
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Tab Panel 3: Class Wise Comparison */}
          {selectedReportTab === 3 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Year"
                    type="number"
                    value={reportFilters.year}
                    onChange={(e) => setReportFilters({ ...reportFilters, year: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<Assessment />}
                    onClick={() => handleGenerateReport('class-wise-comparison')}
                    fullWidth
                    sx={{
                      height: '40px',
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5a6fd6' },
                      textTransform: 'none'
                    }}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>

              {reportLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!reportLoading && reportData['class-wise-comparison'] && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Year: {reportFilters.year}
                  </Typography>
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Class</strong></TableCell>
                          <TableCell><strong>Total Admissions</strong></TableCell>
                          <TableCell><strong>Enrolled</strong></TableCell>
                          <TableCell><strong>Pending</strong></TableCell>
                          <TableCell><strong>Struck Off</strong></TableCell>
                          <TableCell><strong>Pass Out</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData['class-wise-comparison'].length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                No data available for the selected criteria
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportData['class-wise-comparison'].map((row, index) => (
                            <TableRow key={index} hover>
                              <TableCell><strong>{row.className}</strong></TableCell>
                              <TableCell><strong>{row.total}</strong></TableCell>
                              <TableCell>
                                <Chip label={row.enrolled || 0} color="primary" size="small" />
                              </TableCell>
                              <TableCell>{row.pending || 0}</TableCell>
                              <TableCell>{row.struckoff || 0}</TableCell>
                              <TableCell>{row.passout || 0}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!reportLoading && !reportData['class-wise-comparison'] && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <School sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Select year and click "Generate" to view the report
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Tab Panel 4: Date Wise Admission */}
          {selectedReportTab === 4 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={reportFilters.startDate}
                    onChange={(e) => setReportFilters({ ...reportFilters, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={reportFilters.endDate}
                    onChange={(e) => setReportFilters({ ...reportFilters, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Button
                    variant="contained"
                    startIcon={<Assessment />}
                    onClick={() => handleGenerateReport('date-wise-admission')}
                    fullWidth
                    sx={{
                      height: '40px',
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5a6fd6' },
                      textTransform: 'none'
                    }}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>

              {reportLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!reportLoading && reportData['date-wise-admission'] && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {reportFilters.startDate && reportFilters.endDate 
                      ? `Period: ${new Date(reportFilters.startDate).toLocaleDateString()} to ${new Date(reportFilters.endDate).toLocaleDateString()}`
                      : 'Period: All Time'}
                  </Typography>
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Application No.</strong></TableCell>
                          <TableCell><strong>Student Name</strong></TableCell>
                          <TableCell><strong>Class</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData['date-wise-admission'].length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                No data available for the selected criteria
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportData['date-wise-admission'].map((row, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Chip label={row.applicationNumber} size="small" variant="outlined" color="primary" />
                              </TableCell>
                              <TableCell><strong>{row.studentName}</strong></TableCell>
                              <TableCell>{row.className}</TableCell>
                              <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={row.status.replace('_', ' ').toUpperCase()} 
                                  size="small" 
                                  color={getStatusColor(row.status)}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!reportLoading && !reportData['date-wise-admission'] && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ListIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Select date range and click "Generate" to view the report
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Tab Panel 5: Student's Strength Month Wise */}
          {selectedReportTab === 5 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Month"
                    value={reportFilters.month}
                    onChange={(e) => setReportFilters({ ...reportFilters, month: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">All Months</MenuItem>
                    <MenuItem value="1">January</MenuItem>
                    <MenuItem value="2">February</MenuItem>
                    <MenuItem value="3">March</MenuItem>
                    <MenuItem value="4">April</MenuItem>
                    <MenuItem value="5">May</MenuItem>
                    <MenuItem value="6">June</MenuItem>
                    <MenuItem value="7">July</MenuItem>
                    <MenuItem value="8">August</MenuItem>
                    <MenuItem value="9">September</MenuItem>
                    <MenuItem value="10">October</MenuItem>
                    <MenuItem value="11">November</MenuItem>
                    <MenuItem value="12">December</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Year"
                    type="number"
                    value={reportFilters.year}
                    onChange={(e) => setReportFilters({ ...reportFilters, year: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<Assessment />}
                    onClick={() => handleGenerateReport('student-strength-month-wise')}
                    fullWidth
                    sx={{
                      height: '40px',
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5a6fd6' },
                      textTransform: 'none'
                    }}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>

              {reportLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!reportLoading && reportData['student-strength-month-wise'] && (
                <Box>
                  {/* Summary cards */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#667eea15', border: '1px solid #667eea30' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Total Students</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#667eea">
                            {reportData['student-strength-month-wise'].summary?.total || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#4caf5015', border: '1px solid #4caf5030' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Enrolled</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#4caf50">
                            {reportData['student-strength-month-wise'].summary?.enrolled || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#2196f315', border: '1px solid #2196f330' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Male</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#2196f3">
                            {reportData['student-strength-month-wise'].summary?.male || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card elevation={0} sx={{ bgcolor: '#e91e6315', border: '1px solid #e91e6330' }}>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">Female</Typography>
                          <Typography variant="h4" fontWeight="bold" color="#e91e63">
                            {reportData['student-strength-month-wise'].summary?.female || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Table */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Class</strong></TableCell>
                          <TableCell><strong>Section</strong></TableCell>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell><strong>Enrolled</strong></TableCell>
                          <TableCell><strong>Male</strong></TableCell>
                          <TableCell><strong>Female</strong></TableCell>
                          <TableCell><strong>Other</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData['student-strength-month-wise'].rows?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                No data available for the selected criteria
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportData['student-strength-month-wise'].rows?.map((row, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{row.className}</TableCell>
                              <TableCell>{row.sectionName}</TableCell>
                              <TableCell><strong>{row.total}</strong></TableCell>
                              <TableCell>{row.enrolled}</TableCell>
                              <TableCell>{row.male}</TableCell>
                              <TableCell>{row.female}</TableCell>
                              <TableCell>{row.other}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!reportLoading && !reportData['student-strength-month-wise'] && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <TrendingUp sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Select month & year and click &quot;Generate&quot; to view the report
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

        </Box>
      )}

      {/* Section: Register */}
      {activeSection === 'register' && (
        <Box sx={{ mx: 3 }}>
          <Paper sx={{ p: 3, mt: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                ADMISSIONS REGISTER
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Bulk Delete Button */}
                {selectedAdmissions.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Delete />}
                    onClick={handleBulkPermanentDelete}
                    sx={{
                      bgcolor: '#dc3545',
                      '&:hover': { bgcolor: '#c82333' },
                      textTransform: 'none',
                    }}
                  >
                    Delete Selected ({selectedAdmissions.length})
                  </Button>
                )}

                {/* Bulk Status Update Button */}
                {selectedAdmissions.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleOpenBulkStatusDialog}
                    sx={{
                      bgcolor: '#ff9800',
                      '&:hover': { bgcolor: '#f57c00' },
                      textTransform: 'none',
                      ml: 1
                    }}
                  >
                    Update Status ({selectedAdmissions.length})
                  </Button>
                )}

                {/* Export Button */}
                {selectedAdmissions.length > 0 && (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Download />}
                    onClick={handleExport}
                    sx={{
                      borderColor: '#2e7d32',
                      color: '#2e7d32',
                      '&:hover': {
                        borderColor: '#1b5e20',
                        bgcolor: '#2e7d3215',
                      },
                      textTransform: 'none',
                    }}
                  >
                    Export to Excel
                  </Button>
                )}
              </Box>
            </Box>



            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    Filters
                  </Typography>
                  {filterNewAdmissions && (
                    <Chip
                      label="Last 30 Days Admissions"
                      onDelete={() => {
                        setFilterNewAdmissions(false);
                        navigate('/admissions/register');
                      }}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
                {(filterStudentName || filterRollNumber || filterClass || filterStatus || searchTerm || filterNewAdmissions) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setFilterStudentName('');
                      setFilterRollNumber('');
                      setFilterClass('');
                      setFilterStatus('');
                      setSearchTerm('');
                      setFilterNewAdmissions(false);
                      navigate('/admissions/register');
                    }}
                    sx={{ textTransform: 'none', color: '#667eea', fontWeight: 600 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: '2fr 1.5fr 1.5fr 1.5fr'
                  },
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search by student name..."
                  value={filterStudentName}
                  onChange={(e) => setFilterStudentName(e.target.value)}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Roll Number"
                  placeholder="Search roll no..."
                  value={filterRollNumber}
                  onChange={(e) => setFilterRollNumber(e.target.value)}
                  size="small"
                />
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Class"
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">All Classes</MenuItem>
                  {Array.from(new Map(admissions
                    .filter(a => a.class)
                    .map(a => {
                      const classId = a.class?._id || a.class;
                      const className = a.class?.name || 'Unknown';
                      return [classId, { _id: classId, name: className }];
                    })
                  ).values()).map((cls) => (
                    <MenuItem key={cls._id} value={cls._id}>
                      {cls.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="enrolled">Enrolled</MenuItem>
                  <MenuItem value="struckoff">Struck Off</MenuItem>
                  <MenuItem value="passout">Passout</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : (
              <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#667eea' }}>
                      <TableCell padding="checkbox" sx={{ color: 'white' }}>
                        <Checkbox
                          sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                          indeterminate={selectedAdmissions.length > 0 && selectedAdmissions.length < sortedRegisterAdmissions.length}
                          checked={sortedRegisterAdmissions.length > 0 && selectedAdmissions.length === sortedRegisterAdmissions.length}
                          onChange={(e) => handleSelectAll(e, sortedRegisterAdmissions)}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sr No</TableCell>
                      
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'admissionDate'}
                          direction={registerSortConfig.key === 'admissionDate' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('admissionDate')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Date Of Admission
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'appNumber'}
                          direction={registerSortConfig.key === 'appNumber' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('appNumber')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Admission No
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'rollNumber'}
                          direction={registerSortConfig.key === 'rollNumber' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('rollNumber')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Roll No
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'studentName'}
                          direction={registerSortConfig.key === 'studentName' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('studentName')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Student Name
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'fatherName'}
                          direction={registerSortConfig.key === 'fatherName' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('fatherName')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Father Name
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'dateOfBirth'}
                          direction={registerSortConfig.key === 'dateOfBirth' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('dateOfBirth')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Date Of Birth
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'phone'}
                          direction={registerSortConfig.key === 'phone' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('phone')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Phone No
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'status'}
                          direction={registerSortConfig.key === 'status' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('status')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={registerSortConfig.key === 'className'}
                          direction={registerSortConfig.key === 'className' ? registerSortConfig.direction : 'asc'}
                          onClick={() => handleRegisterSort('className')}
                          sx={{
                            color: 'white !important',
                            fontWeight: 'bold',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          Class To Which Admitted
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRegisterAdmissions
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((admission, idx) => {
                        const isItemSelected = isSelected(admission._id);
                        const labelId = `enhanced-table-checkbox-${idx}`;
                        const actualIndex = page * rowsPerPage + idx;
                        const studentName = admission.personalInfo?.name || admission.name || admission.studentName || admission.firstName || '';
                        const rawDob = admission.personalInfo?.dateOfBirth || admission.dateOfBirth || admission.dob;
                        const dateOfBirth = rawDob 
                          ? new Date(rawDob).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'N/A';
                        const dateOfAdmissionRaw = admission.admissionDate || admission.createdAt;
                        const admissionDate = dateOfAdmissionRaw 
                          ? new Date(dateOfAdmissionRaw).toLocaleDateString('en-GB')
                          : 'N/A';
                        const className = admission.class?.name || admission.className || admission.program || 'N/A';
                        const appNumber = admission.applicationNumber || admission.admissionNo || admission.admissionNumber || 'N/A';
                        const rollNumber = admission.studentId?.rollNumber || admission.rollNumber || 'N/A';
                        const phone = admission.contactInfo?.phone || 
                                      admission.contactDetails?.phone || 
                                      admission.contactInfo?.alternatePhone || 
                                      admission.guardianInfo?.father?.mobileNumber || 
                                      admission.guardianInfo?.mother?.mobileNumber || 
                                      admission.guardianInfo?.mobileNumber || 
                                      admission.phone || 
                                      admission.mobileNumber || 
                                      'N/A';
                        
                        return (
                          <TableRow 
                            key={admission._id} 
                            hover
                            sx={{
                              bgcolor: admission.isActive === false ? '#f8f9fa' : 'inherit',
                              opacity: admission.isActive === false ? 0.7 : 1
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedAdmissions.includes(admission._id)}
                                onChange={() => handleSelectAdmission(admission._id)}
                              />
                            </TableCell>
                            <TableCell>{actualIndex + 1}</TableCell>
                            <TableCell>{admissionDate}</TableCell>
                            <TableCell>
                              <Box sx={{ textDecoration: admission.isActive === false ? 'line-through' : 'none' }}>
                                {appNumber}
                              </Box>
                            </TableCell>
                            <TableCell>{rollNumber}</TableCell>
                            <TableCell>
                              <Box sx={{ textDecoration: admission.isActive === false ? 'line-through' : 'none' }}>
                                {studentName || 'N/A'}
                              </Box>
                            </TableCell>
                            <TableCell>{admission.guardianInfo?.fatherName || admission.fatherName || 'N/A'}</TableCell>
                            <TableCell>{dateOfBirth}</TableCell>
                            <TableCell>{phone}</TableCell>
                            <TableCell>
                              <Chip
                                label={admission.status || 'pending'}
                                size="small"
                                color={getStatusColor(admission.status)}
                              />
                            </TableCell>
                            <TableCell>{className}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/admissions/edit/${admission._id}`)}
                                  sx={{ color: '#28a745' }}
                                  title="View"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/admissions/edit/${admission._id}`)}
                                  sx={{ color: '#0d6efd' }}
                                  title="Edit"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAdmission(admission._id, studentName || admission.applicationNumber)}
                                  sx={{ color: '#dc3545' }}
                                  title="Delete"
                                  disabled={admission.status === 'enrolled'}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[12, 24, 60, 100]}
                component="div"
                count={filteredRegisterAdmissions.length}
                rowsPerPage={registerPagination.rowsPerPage}
                page={registerPagination.page}
                onPageChange={registerPagination.handleChangePage}
                onRowsPerPageChange={registerPagination.handleChangeRowsPerPage}
              />
              </>
            )}
          </Paper>
        </Box>
      )}



      {/* Section: Analytics */}
      {activeSection === 'analytics' && (
        <Box sx={{ px: 3 }}>
          {getInstitutionId() ? (
            <AdmissionCharts 
              filters={{ institution: getInstitutionId() }} 
            />
          ) : (
            <Alert severity="info">
              Please select an institution to view analytics.
            </Alert>
          )}
        </Box>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: '', remarks: '', effectiveDate: new Date().toISOString().split('T')[0] })}>
        <DialogTitle>
          {actionDialog.type === 'approve' && 'Approve Admission'}
          {actionDialog.type === 'reject' && 'Reject Admission'}
          {actionDialog.type === 'enroll' && 'Enroll Student'}
          {actionDialog.type === 'struck_off' && 'Mark Struck Off'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={actionDialog.remarks}
            onChange={(e) => setActionDialog({ ...actionDialog, remarks: e.target.value })}
            sx={{ mt: 2 }}
          />
          {actionDialog.type === 'struck_off' && (
            <TextField
              fullWidth
              type="date"
              label="Effective Struck-Off Date"
              value={actionDialog.effectiveDate}
              onChange={(e) => setActionDialog({ ...actionDialog, effectiveDate: e.target.value })}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              helperText="Student will not appear in voucher generation from this month onwards"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', remarks: '', effectiveDate: new Date().toISOString().split('T')[0] })}>
            Cancel
          </Button>
          <Button onClick={() => handleAction(actionDialog.type)} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkStatusDialogOpen} onClose={handleCloseBulkStatusDialog}>
        <DialogTitle>Update Status for {selectedAdmissions.length} Students</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={selectedBulkStatus}
                onChange={(e) => setSelectedBulkStatus(e.target.value)}
                label="New Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="enrolled">Enrolled</MenuItem>
                <MenuItem value="struckoff">Struck Off</MenuItem>
                <MenuItem value="passout">Passout</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks (Optional)"
              value={bulkStatusRemarks}
              onChange={(e) => setBulkStatusRemarks(e.target.value)}
              placeholder="Reason for status change..."
              size="small"
            />
            {(selectedBulkStatus === 'struckoff' || selectedBulkStatus === 'struck_off') && (
              <TextField
                fullWidth
                type="date"
                label="Effective Struck-Off Date"
                value={bulkEffectiveDate}
                onChange={(e) => setBulkEffectiveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                size="small"
                sx={{ mt: 2 }}
                helperText="Student(s) will not appear in voucher generation from this month onwards"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkStatusDialog}>Cancel</Button>
          <Button 
            onClick={handleBulkStatusUpdate} 
            variant="contained" 
            color="primary"
            disabled={!selectedBulkStatus || loading}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admissions;
